import { joinNevisignUrl } from "@nevisign/reader-core";
import { faceCaptureFormData, type FaceCapture } from "../face-capture";
import type { GatewayView } from "./types";
import { notifySmsFromHeaders } from "../sms-notice";

const API_BASE = (import.meta.env.VITE_API_URL?.trim() || "/api/v1").replace(
  /\/$/,
  "",
);

const EMPTY_OR_DOWN_FA =
  "پاسخ خالی از سرور دریافت شد. اگر API در حال راه‌اندازی مجدد است چند لحظه صبر کنید و دوباره تلاش کنید.";
const UNREADABLE_JSON_FA =
  "پاسخ سرور قابل خواندن نبود. چند لحظه صبر کنید و دوباره تلاش کنید.";

function errorMessageFromPayload(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") return fallback;
  const obj = data as { error?: unknown; message?: unknown };
  const nested =
    obj.error && typeof obj.error === "object"
      ? (obj.error as { message?: unknown }).message
      : undefined;
  const raw =
    (typeof nested === "string" && nested.trim()) ||
    (typeof obj.error === "string" && obj.error.trim()) ||
    (typeof obj.message === "string" && obj.message.trim()) ||
    "";
  if (!raw) return fallback;
  const rate = raw.match(/Rate limit exceeded,\s*retry in\s+(.+)/i);
  if (rate) {
    return `تعداد درخواست‌ها زیاد است. حدود ${rate[1]} دیگر دوباره تلاش کنید.`;
  }
  if (/too many requests/i.test(raw)) {
    return "تعداد درخواست‌ها زیاد است. کمی صبر کنید و دوباره تلاش کنید.";
  }
  return raw;
}

export async function readJson<T>(res: Response): Promise<T> {
  notifySmsFromHeaders(res.headers);
  const text = await res.text();
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error(
      res.status >= 500 || res.status === 0
        ? EMPTY_OR_DOWN_FA
        : `ارتباط با درگاه امضا برقرار نشد (کد ${res.status}). سرویس API را بررسی کنید.`,
    );
  }
  const contentType = (res.headers.get("content-type") ?? "").toLowerCase();
  if (contentType.includes("text/html") || trimmed.startsWith("<!")) {
    throw new Error("سرور به‌جای JSON پاسخ HTML داد. API را بررسی کنید.");
  }
  let data: T & { error?: unknown };
  try {
    data = JSON.parse(trimmed) as T & { error?: unknown };
  } catch {
    throw new Error(UNREADABLE_JSON_FA);
  }
  if (
    !res.ok ||
    (data && typeof data === "object" && "error" in data && data.error)
  ) {
    throw new Error(errorMessageFromPayload(data, "درخواست ناموفق بود"));
  }
  return data;
}

export function gatewayBasePath(contractId: string, token: string): string {
  return joinNevisignUrl(
    API_BASE,
    `/sign-gateway/${encodeURIComponent(contractId)}/${encodeURIComponent(token)}`,
  );
}

export async function fetchGatewayView(
  contractId: string,
  token: string,
): Promise<GatewayView> {
  const res = await fetch(gatewayBasePath(contractId, token));
  const data = await readJson<GatewayView | { error?: string }>(res);
  if (!("ok" in data) || !data.ok) {
    throw new Error(
      ("error" in data && data.error) || "لینک امضا معتبر نیست یا منقضی شده است",
    );
  }
  return data;
}

export async function postGatewayJson<T>(
  contractId: string,
  token: string,
  path: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(`${gatewayBasePath(contractId, token)}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return readJson<T>(res);
}

export async function markGatewaySeen(
  contractId: string,
  token: string,
): Promise<GatewayView | null> {
  try {
    return await postGatewayJson<GatewayView>(contractId, token, "/act", {
      action: "seen",
    });
  } catch {
    return null;
  }
}

export async function uploadGatewayFaceMatch(
  contractId: string,
  token: string,
  capture: FaceCapture,
): Promise<GatewayView> {
  const res = await fetch(
    `${gatewayBasePath(contractId, token)}/identity/face-match`,
    { method: "POST", body: faceCaptureFormData(capture) },
  );
  const data = await readJson<GatewayView | { error?: string }>(res);
  if (!("ok" in data) || !data.ok) {
    throw new Error(
      ("error" in data && typeof data.error === "string" && data.error) ||
        "احراز هویت ویدیویی ناموفق بود",
    );
  }
  return data;
}
